"use client";

import { CardDescription, CardTitle, FieldGroup, useToast } from "@atlas/core";
import {
  FilterBuilder,
  type SearchResponse,
  useTranslation,
  useZodSearchParams,
} from "@atlas/sdk";
import { CatalogMode, IHubReviewStatus } from "@enums";
import type { IHubComment, IHubReview } from "@models";
import {
  useCreateHubComment,
  useCreateHubReview,
  useHubReviewsByCatalog,
  useSearchHubComments,
} from "@query";
import type { InfiniteData } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { Comments, type ICommentsComposerProps } from "./Comments";
import { PostCatalogRating } from "./PostCatalogRating";

const searchParamsSchema = z.object({
  tab: z.string().optional(),
  fromTab: z.string().optional(),
});

interface ICatalogCommentsProps {
  catalogId?: number;
  catalogVersion?: number;
  mode?: CatalogMode;
}

function prbReviewsToHubComments(
  reviews: IHubReview[],
  t: ReturnType<typeof useTranslation>["t"],
): IHubComment[] {
  return reviews.map((r) => {
    const pending = r.status === IHubReviewStatus.InReview;
    const commentBy = r.reviewedBy?.trim() || "—";

    const commentBody = r.reviewComments?.trim() ?? "";
    const emptyNoComment = t("hub.catalog_manager.prb_review_no_comment", {
      defaultValue: "No comment provided.",
    });
    const body = commentBody ? commentBody : "";
    const emptyBodyLabel =
      !pending && !commentBody ? emptyNoComment : undefined;
    const content = body.trim() !== "" ? body : (emptyBodyLabel ?? "");

    return {
      commentId: r.reviewId,
      catalogId: r.catalogId,
      content,
      commentBy,
      commentDatetime: r.submittedDatetime ?? r.reviewedDatetime,
    };
  });
}

export function CatalogComments({
  catalogId,
  catalogVersion,
  mode = CatalogMode.Details,
}: ICatalogCommentsProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { tab, fromTab } = useZodSearchParams(searchParamsSchema);
  const effectiveId = catalogId ?? 0;
  const isReviewMode = mode === CatalogMode.Review;
  const isSubscriptionsTab = tab === "subscriptions";
  const isDeploymentsTab = fromTab === "deployments";

  const showCommentComposer = useMemo(
    () => isReviewMode || isSubscriptionsTab || isDeploymentsTab,
    [isReviewMode, isSubscriptionsTab, isDeploymentsTab],
  );

  const [commentDraft, setCommentDraft] = useState("");
  const { mutate: postComment, isPending: isPostingComment } =
    useCreateHubComment();
  const { mutateAsync: createReviewRow, isPending: isPostingReview } =
    useCreateHubReview();

  const {
    data: comments = [],
    isLoading: commentsLoading,
    isError: commentsError,
  } = useSearchHubComments(
    {
      filter: new FilterBuilder()
        .equals("catalogId", effectiveId)
        .equals("version", catalogVersion?.toString() ?? ""),
    },
    {
      ...(isReviewMode ? { enabled: false } : {}),
      enabled: true,
    },
  );

  const {
    data: reviews = [],
    isLoading: prbLoading,
    isError: prbError,
  } = useHubReviewsByCatalog(effectiveId, {
    enabled: isReviewMode,
    ...(isReviewMode ? { refetchOnMount: "always" as const } : {}),
  });

  const sortedPrb = useMemo(() => {
    const scoped =
      catalogVersion != null
        ? reviews.filter((r: IHubReview) => r.version === catalogVersion)
        : reviews;
    return [...scoped].sort((a, b) => {
      const ta = a.reviewedDatetime || a.submittedDatetime || "";
      const tb = b.reviewedDatetime || b.submittedDatetime || "";
      const dateCmp = tb.localeCompare(ta);
      if (dateCmp !== 0) return dateCmp;
      const va = a.version ?? 0;
      const vb = b.version ?? 0;
      return vb - va;
    });
  }, [reviews, catalogVersion]);
  const commentsData = useMemo(
    () =>
      (
        comments as unknown as InfiniteData<SearchResponse<IHubComment>>
      )?.pages?.flatMap((page) => page.items) ?? [],
    [comments],
  );
  const commentsForList = useMemo(() => {
    if (isReviewMode) return prbReviewsToHubComments(sortedPrb, t);
    return [...commentsData].sort((a, b) => {
      const ta = a.commentDatetime ?? "";
      const tb = b.commentDatetime ?? "";
      return tb.localeCompare(ta);
    });
  }, [isReviewMode, sortedPrb, commentsData, t]);

  const submitCatalogComment = useCallback(
    (text: string) => {
      const content = text.trim();
      if (!content || effectiveId <= 0) return;
      postComment(
        {
          catalogId: effectiveId,
          content,
          version: catalogVersion,
        },
        {
          onSuccess: () => {
            setCommentDraft("");
            toast.add({
              title: t("hub.catalog_details.comment_posted", {
                defaultValue: "Comment posted",
              }),
              data: { type: "success" },
            });
          },
          onError: () => {
            toast.add({
              title: t("hub.catalog_details.comment_post_error", {
                defaultValue: "Could not post comment",
              }),
              data: { type: "error" },
            });
          },
        },
      );
    },
    [effectiveId, postComment, toast, t],
  );

  const submitPrbReview = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || effectiveId <= 0) return;
      if (catalogVersion == null) {
        toast.add({
          title: t("hub.catalog_manager.prb_review_missing_version", {
            defaultValue:
              "Catalog version is required to post a review comment",
          }),
          data: { type: "error" },
        });
        return;
      }
      try {
        await createReviewRow({
          catalogId: effectiveId,
          status: IHubReviewStatus.InReview,
          reviewComments: trimmed,
          version: catalogVersion,
          reviewType: IHubReviewStatus.InReview,
        });
        setCommentDraft("");
        toast.add({
          title: t("hub.catalog_manager.prb_review_add_success", {
            defaultValue: "Comment saved",
          }),
          data: { type: "success" },
        });
      } catch {
        toast.add({
          title: t("hub.catalog_manager.prb_review_add_error", {
            defaultValue: "Could not save comment",
          }),
          data: { type: "error" },
        });
      }
    },
    [effectiveId, catalogVersion, createReviewRow, toast, t],
  );

  const handleComposerSubmit = useCallback(
    (text: string) => {
      if (isReviewMode) void submitPrbReview(text);
      else submitCatalogComment(text);
    },
    [isReviewMode, submitPrbReview, submitCatalogComment],
  );

  const composer = useMemo((): ICommentsComposerProps | null => {
    if (!showCommentComposer) return null;
    const prbPlaceholder = t(
      "hub.catalog_manager.reviews_thread_add_placeholder",
      {
        defaultValue:
          "Write a comment, then press Enter or send. Your work is kept on this review.",
      },
    );
    return {
      draft: commentDraft,
      onDraftChange: setCommentDraft,
      onSubmit: handleComposerSubmit,
      isPosting: isReviewMode ? isPostingReview : isPostingComment,
      promptPlaceholder: isReviewMode ? prbPlaceholder : undefined,
    };
  }, [
    showCommentComposer,
    commentDraft,
    setCommentDraft,
    handleComposerSubmit,
    isReviewMode,
    isPostingReview,
    isPostingComment,
    t,
  ]);

  return (
    <FieldGroup>
      <CardTitle>
        {isReviewMode
          ? t("hub.catalog_details.review_comments", {
              defaultValue: "Review Comments",
            })
          : t("hub.catalog_details.tab_comments", { defaultValue: "Comments" })}
      </CardTitle>
      <CardDescription>
        {commentsForList.length === 0
          ? t("hub.catalog_details.no_comments", {
              defaultValue: "No comments for this catalog.",
            })
          : ""}
      </CardDescription>
      {!isReviewMode &&
        catalogVersion != null &&
        (isSubscriptionsTab || isDeploymentsTab) && (
          <PostCatalogRating
            catalogId={effectiveId}
            catalogVersion={catalogVersion}
          />
        )}
      <Comments
        catalogId={effectiveId}
        comments={commentsForList}
        loading={isReviewMode ? prbLoading : commentsLoading}
        error={isReviewMode ? prbError : commentsError}
        composer={composer}
      />
    </FieldGroup>
  );
}
